<template>
  <div>
    <!-- Bordereaux Filters Section -->
    <div class="filters-section">
      <div class="filters-header">
        <h4>
          <a-icon type="filter" class="filter-icon" />
          {{ $t("all.filtresEtRecherche") }}
        </h4>
        <div class="action-button-group">
          <a-button
            icon="plus"
            type="primary"
            @click="openCreateBordereauModal"
            class="action-button"
          >
            {{ $t("tresorerie.nouveauBordereau") }}
          </a-button>
          <a-button
            icon="file-pdf"
            type="primary"
            @click="generateBordereauPdf"
            :loading="printLoading"
            :disabled="printLoading"
            class="action-button"
          >
            {{ $t("action.imprimer") }}
          </a-button>
          <a-button icon="reload" @click="resetFilters" class="action-button">
            {{ $t("action.reinitialiser") }}
          </a-button>
        </div>
      </div>

      <a-row :gutter="[16, 16]" class="align-items-end">
        <!-- Search Input -->
        <a-col :xs="24" :sm="8" :md="8" :lg="8">
          <div class="filter-item">
            <label class="filter-label">{{ $t("tresorerie.recherche") }}</label>
            <a-input
              v-model="searchQuery"
              :placeholder="$t('tresorerie.recherchreParNumeroDescription')"
              @input="handleSearchInput"
              allow-clear
              class="search-input"
            >
              <a-icon slot="prefix" type="search" />
            </a-input>
          </div>
        </a-col>

        <!-- Statut Filter -->
        <a-col :xs="24" :sm="8" :md="8" :lg="8">
          <div class="filter-item">
            <label class="filter-label">{{ $t("tresorerie.statut") }}</label>
            <a-select
              v-model="statutFilter"
              :placeholder="$t('tresorerie.statutDuBordereau')"
              @change="handleFilter"
              allow-clear
              class="filter-select"
            >
              <a-select-option value="PENDING">
                <a-icon type="clock-circle" class="status-icon" />
                {{ $t("status.PENDING") }}
              </a-select-option>
              <a-select-option value="VALIDATED">
                <a-icon type="check-circle" class="status-icon active" />
                {{ $t("status.VALIDATED") }}
              </a-select-option>
              <a-select-option value="REJECTED">
                <a-icon type="close-circle" class="status-icon inactive" />
                {{ $t("status.REJECTED") }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>

        <!-- Statut Depot Filter -->
        <a-col :xs="24" :sm="8" :md="8" :lg="8">
          <div class="filter-item">
            <label class="filter-label">{{
              $t("tresorerie.statutDepot")
            }}</label>
            <a-select
              v-model="statutDepotFilter"
              :placeholder="$t('tresorerie.statutDuDepot')"
              @change="handleFilter"
              allow-clear
              class="filter-select"
            >
              <a-select-option value="NON_DEPOSE">
                <a-icon type="stop" class="status-icon" />
                {{ $t("tresorerie.nonDepose") }}
              </a-select-option>
              <a-select-option value="DEPOSE">
                <a-icon type="check" class="status-icon active" />
                {{ $t("tresorerie.depose") }}
              </a-select-option>
              <a-select-option value="TRAITE">
                <a-icon type="check-circle" class="status-icon active" />
                {{ $t("tresorerie.traite") }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>
      </a-row>
    </div>

    <!-- Bordereaux Table -->
    <div class="table-container">
      <a-table
        :loading="tableLoading"
        :rowKey="'_id'"
        :columns="columns"
        :data-source="data"
        :pagination="pagination"
        :scroll="{ x: 'max-content' }"
        @change="tableChanged"
        size="middle"
        :row-class-name="getRowClassName"
      >
        <template slot="numero" slot-scope="text">
          <span class="numero-cell">
            <a-icon type="file-text" class="action-icon text-primary" />
            {{ text }}
          </span>
        </template>
        <template slot="type" slot-scope="text">
          <a-tag :color="text === 'CHEQUE' ? 'blue' : 'green'" class="type-tag">
            <a-icon
              :type="text === 'CHEQUE' ? 'bank' : 'file-text'"
              style="margin-right: 4px"
            />
            {{
              text === "CHEQUE"
                ? $t("tresorerie.cheques")
                : $t("tresorerie.traites")
            }}
          </a-tag>
        </template>
        <template slot="createdAt" slot-scope="text">
          <span class="date-cell">
            {{ moment(new Date(text)).format("DD/MM/YYYY") }}
          </span>
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="getChequeStatutColor(text)" class="type-tag">
            {{ $t("status." + text) }}
          </a-tag>
        </template>
        <template slot="statutDepot" slot-scope="text">
          <a-tag :color="getStatutDepotColor(text)" class="type-tag">
            {{ $t("status." + text) }}
          </a-tag>
        </template>
        <template slot="caisseBank" slot-scope="text, record">
          <span v-if="record.caisseBank" class="caisse-cell">
            <a-icon type="bank" class="action-icon text-success" />
            {{ record.caisseBank.name }}
            <br />
            <small class="text-muted"
              >({{ record.caisseBank.reference }})</small
            >
          </span>
          <span v-else>--</span>
        </template>
        <template slot="montantTotal" slot-scope="text">
          <span class="amount-cell">
            {{ formatAmount(text) }}
          </span>
        </template>
        <template slot="nombreCheques" slot-scope="text, record">
          <span
            class="cheque-count-cell clickable"
            @click="() => openChequesModal(record)"
            style="cursor: pointer; color: #1890ff"
          >
            <a-icon
              :type="record.type === 'CHEQUE' ? 'credit-card' : 'file-text'"
              class="action-icon"
            />
            {{ text }}
            {{
              record.type === "CHEQUE"
                ? $t("tresorerie.chequesPluriel")
                : $t("tresorerie.traitesPluriel")
            }}
            <br />
            <small class="text-muted">
              {{ record.nombreChequesValides }} {{ $t("tresorerie.valides") }} /
              {{ record.nombreChequesEnAttente }}
              {{ $t("tresorerie.enAttente") }}
            </small>
          </span>
        </template>
        <template slot="createdBy" slot-scope="text, record">
          <span
            v-if="record.createdBy && record.createdBy.userName"
            class="user-cell"
          >
            <a-icon type="user" class="action-icon" style="color: #722ed1" />
            {{ record.createdBy.userName || "--" }}
          </span>
          <span v-else>--</span>
        </template>
        <template slot="actions" slot-scope="text, record">
          <div style="display: flex; gap: 4px; align-items: center">
            <a-button
              type="default"
              size="small"
              @click="() => viewDetails(record)"
              style="border-radius: 6px; padding: 0 8px; height: 24px"
            >
              <a-icon type="eye" />
            </a-button>
            <a-button
              type="primary"
              size="small"
              @click="() => openEditModal(record)"
              style="border-radius: 6px; padding: 0 8px; height: 24px"
            >
              <a-icon type="edit" />
            </a-button>

            <a-button
              type="primary"
              size="small"
              @click="() => printSingleBordereau(record)"
              style="border-radius: 6px; padding: 0 8px; height: 24px"
            >
              <a-icon type="printer" />
            </a-button>
            <a-button
              type="default"
              size="small"
              @click="() => openChequesModal(record)"
              style="border-radius: 6px; padding: 0 8px; height: 24px"
              :disabled="!record.nombreCheques || record.nombreCheques === 0"
              :title="
                record.nombreCheques > 0
                  ? $t('tresorerie.gererLesCheques')
                  : $t('tresorerie.aucunCheque')
              "
            >
              <a-icon type="credit-card" />
            </a-button>
            <a-popconfirm
              :title="$t('tresorerie.confirmationSuppressionBordereau')"
              @confirm="() => deleteBordereau(record)"
              :ok-text="$t('all.oui')"
              :cancel-text="$t('all.non')"
            >
              <a-button
                type="danger"
                size="small"
                style="border-radius: 6px; padding: 0 8px; height: 24px"
                :disabled="record.nombreChequesValides > 0"
                :title="
                  record.nombreChequesValides > 0
                    ? $t('tresorerie.impossibleSupprimerBordereau')
                    : ''
                "
              >
                <a-icon type="delete" />
              </a-button>
            </a-popconfirm>
          </div>
        </template>
      </a-table>
    </div>

    <!-- Modals -->
    <create-bordereau-modal
      :visible="createModalVisible"
      @close="handleCreateModalClose"
      @created="handleBordereauCreated"
    />
    <edit-bordereau-modal
      :visible="editModalVisible"
      :bordereauData="selectedBordereau"
      @close="handleEditModalClose"
      @updated="handleBordereauUpdated"
    />
    <view-bordereau-details-modal
      :visible="viewModalVisible"
      :bordereauData="selectedBordereau"
      :settings="settings"
      :user="user"
      @close="handleViewModalClose"
    />
    <cheques-management-modal
      :visible="chequesModalVisible"
      :bordereauData="selectedBordereau"
      @close="handleChequesModalClose"
      @updated="handleChequesUpdated"
    />
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import CreateBordereauModal from "./CreateBordereauModal.vue";
import EditBordereauModal from "./EditBordereauModal.vue";
import ViewBordereauDetailsModal from "./ViewBordereauDetailsModal.vue";
import ChequesManagementModal from "./ChequesManagementModal.vue";
import {
  getChequeStatutColor,
  getStatutDepotColor,
} from "@/utilities/settings";
import { generateBordereauPdf } from "../shared/pdfGenerator";
import { generateSingleBordereauPdf } from "../shared/pdfGenerator";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "BordereauxTab",
  props: {
    settings: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  components: {
    CreateBordereauModal,
    EditBordereauModal,
    ViewBordereauDetailsModal,
    ChequesManagementModal,
  },
  data() {
    return {
      searchQuery: "",
      statutFilter: undefined,
      statutDepotFilter: undefined,
      tableLoading: false,
      data: [],
      searchTimeout: null,
      createModalVisible: false,
      editModalVisible: false,
      viewModalVisible: false,
      chequesModalVisible: false,
      selectedBordereau: {},
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} ${this.$t("tresorerie.sur")} ${total} ${this.$t("tresorerie.elements")}`,
        pageSizeOptions: ["10", "20", "50", "100"],
        size: "default",
        position: "bottom",
        style: { marginTop: "16px" },
      },
      printLoading: false,
      currency: null,
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("tresorerie.numero"),
          dataIndex: "numero",
          key: "numero",
          scopedSlots: { customRender: "numero" },
          width: 150,
        },
        {
          title: this.$t("tresorerie.type"),
          dataIndex: "type",
          key: "type",
          scopedSlots: { customRender: "type" },
          width: 100,
        },
        {
          title: this.$t("tresorerie.creationDate"),
          dataIndex: "createdAt",
          key: "createdAt",
          scopedSlots: { customRender: "createdAt" },
          sorter: true,
          sortDirections: ["descend", "ascend"],
          width: 120,
        },
        {
          title: this.$t("tresorerie.statut"),
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
          width: 100,
        },
        {
          title: this.$t("tresorerie.statutDepot"),
          dataIndex: "statutDepot",
          key: "statutDepot",
          scopedSlots: { customRender: "statutDepot" },
          width: 120,
        },
        {
          title: this.$t("tresorerie.caisse"),
          dataIndex: "caisseBank",
          key: "caisseBank",
          scopedSlots: { customRender: "caisseBank" },
          width: 150,
        },
        {
          title: this.$t("tresorerie.montantTotal"),
          dataIndex: "montantTotal",
          key: "montantTotal",
          scopedSlots: { customRender: "montantTotal" },
          sorter: true,
          sortDirections: ["descend", "ascend"],
          width: 120,
        },
        {
          title: this.$t("tresorerie.numberOfElements"),
          dataIndex: "nombreCheques",
          key: "nombreCheques",
          scopedSlots: { customRender: "nombreCheques" },
          width: 150,
        },
        {
          title: this.$t("recette.doneBy"),
          dataIndex: "createdBy",
          key: "createdBy",
          scopedSlots: { customRender: "createdBy" },
          width: 150,
        },
        {
          title: this.$t("tresorerie.actions"),
          dataIndex: "actions",
          key: "actions",
          fixed: "right",
          scopedSlots: {
            customRender: "actions",
          },
          width: 140,
        },
      ];
    },
  },
  async created() {
    this.currency = getBuildingCurrency();
    await this.fetchData();
  },
  methods: {
    async fetchData(page = 1, pageSize = 10) {
      try {
        this.tableLoading = true;

        const params = {
          page,
          limit: pageSize,
        };

        // Add search query
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        // Add statut filter
        if (this.statutFilter) {
          params.status = this.statutFilter;
        }

        // Add statutDepot filter
        if (this.statutDepotFilter) {
          params.statutDepot = this.statutDepotFilter;
        }

        const response = await apiClient.get("/compV3/bordereau", {
          params,
        });

        this.data = response.data.data;

        // Update pagination
        this.pagination.current = response.data.pagination.currentPage;
        this.pagination.total = response.data.pagination.totalItems;
        this.pagination.pageSize = response.data.pagination.itemsPerPage;

        this.tableLoading = false;
      } catch (error) {
        console.error("Error fetching bordereau data:", error);
        this.tableLoading = false;
        this.$message.error(this.$t("tresorerie.errorLoadingBordereaux"));
      }
    },

    handleSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(() => {
        this.pagination.current = 1;
        this.fetchData();
      }, 500); // 500ms delay
    },

    handleFilter() {
      this.pagination.current = 1;
      this.fetchData();
    },

    resetFilters() {
      // Clear search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchQuery = "";
      this.statutFilter = undefined;
      this.statutDepotFilter = undefined;
      this.pagination.current = 1;
      this.fetchData();
    },

    tableChanged(pagination, filters, sorter, extra) {
      // Handle pagination change
      if (
        pagination.current !== this.pagination.current ||
        pagination.pageSize !== this.pagination.pageSize
      ) {
        this.fetchData(pagination.current, pagination.pageSize);
      }
    },

    getChequeStatutColor,

    getStatutDepotColor,

    // Modal handlers
    openCreateBordereauModal() {
      this.createModalVisible = true;
    },

    handleCreateModalClose() {
      this.createModalVisible = false;
    },

    handleBordereauCreated(bordereau) {
      this.createModalVisible = false;
      this.fetchData(); // Refresh the table
    },

    openEditModal(record) {
      this.selectedBordereau = { ...record };
      this.editModalVisible = true;
    },

    handleEditModalClose() {
      this.editModalVisible = false;
      this.selectedBordereau = {};
    },

    handleBordereauUpdated(bordereau) {
      this.editModalVisible = false;
      this.selectedBordereau = {};
      this.fetchData(); // Refresh the table
    },

    viewDetails(record) {
      this.selectedBordereau = { ...record };
      this.viewModalVisible = true;
    },

    handleViewModalClose() {
      this.viewModalVisible = false;
      this.selectedBordereau = {};
    },

    openChequesModal(record) {
      this.selectedBordereau = { ...record };
      this.chequesModalVisible = true;
    },

    handleChequesModalClose() {
      this.chequesModalVisible = false;
      this.selectedBordereau = {};
    },

    handleChequesUpdated(bordereau) {
      // Don't close the modal, just refresh the table data
      this.fetchData(); // Refresh the table
    },

    async deleteBordereau(record) {
      try {
        // Check if bordereau has validated cheques
        if (record.nombreChequesValides > 0) {
          this.$message.error(
            this.$t(`errorCode.BORDEREAU_VALIDATED_CHEQUES_EXIST`)
          );
          return;
        }

        const response = await apiClient.delete(
          `/compV3/bordereau/${record._id}`
        );

        if (response.data.success) {
          this.$message.success(this.$t("tresorerie.bordereau.supprimeSucces"));
          this.fetchData(); // Refresh the table
        } else {
          this.$message.error(
            response.data.message || this.$t("tresorerie.errorDuringDeletion")
          );
        }
      } catch (error) {
        console.error("Error deleting bordereau:", error);

        // Handle specific error for validated cheques
        if (error.response?.data?.errorCode === "VALIDATED_CHEQUES_EXIST") {
          this.$message.error(
            this.$t(`errorCode.${error.response.data.errorCode}`)
          );
        } else {
          this.$message.error(
            error.response?.data?.message || this.$t("tresorerie.errorDuringDeletion")
          );
        }
      }
    },

    async printSingleBordereau(record) {
      try {
        // Get the full bordereau data with cheques
        const response = await apiClient.get(`/compV3/bordereau/${record._id}`);

        if (response.data.success) {
          const bordereauData = response.data.data;
          const result = generateSingleBordereauPdf(
            bordereauData,
            this.settings,
            this.user,
            this.$t.bind(this),
            this.$i18n.locale
          );

          if (result.success) {
            this.$message.success(result.message);
          } else {
            this.$message.error(result.message);
          }
        } else {
          this.$message.error(this.$t("tresorerie.errorRetrievingBordereau"));
        }
      } catch (error) {
        console.error("Error printing single bordereau:", error);
        this.$message.error(this.$t("tresorerie.errorGeneratingPdf"));
      }
    },

    formatAmount(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },

    getRowClassName(record, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },

    moment,

    async generateBordereauPdf() {
      this.printLoading = true;
      try {
        // Prepare filters object
        const filters = {
          status: this.statutFilter,
          statutDepot: this.statutDepotFilter,
          search: this.searchQuery,
        };

        // Generate PDF using the utility
        const result = generateBordereauPdf({
          data: this.data,
          settings: this.settings,
          user: this.user,
          filters: filters,
          t: this.$t.bind(this),
          locale: this.$i18n.locale,
        });

        if (result.success) {
          this.$message.success(result.message);
        } else {
          this.$message.error(result.message);
        }
      } catch (error) {
        console.error("Error generating bordereau PDF:", error);
        this.$message.error(this.$t("tresorerie.errorGeneratingPdf"));
      } finally {
        this.printLoading = false;
      }
    },
  },
};
</script>

<style>
@import "../../../common.css";
</style>
